import { useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/core';
import { NodeSelection } from '@tiptap/pm/state';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowDown, ArrowUp, ChevronRight, Copy, GripVertical, Plus } from 'lucide-react';
import { toast } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import { formatCurrencyRange } from '@/lib/format';
import { pricingTypes } from '@/theme/uboTheme';
import {
  addInvestmentPhaseToPage,
  duplicateInvestmentPhase,
  investmentDestinations,
  investmentPhaseRows,
  investmentPhaseTotal,
  reorderInvestmentPhases,
  revealInvestmentPhase,
  type InvestmentDestination,
} from '@/editor/quoteWorkspace';

function OverviewItem({
  phase,
  index,
  count,
  rowCount,
  total,
  onOpen,
  onMove,
  onDuplicate,
  added,
}: {
  phase: InvestmentDestination;
  index: number;
  count: number;
  rowCount: number;
  total: string;
  onOpen: () => void;
  onMove: (direction: number) => void;
  onDuplicate: () => void;
  added: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: phase.tableId });
  return (
    <li
      ref={setNodeRef}
      className="workspace-overview-item"
      data-overview-id={phase.tableId}
      data-added={added || undefined}
      data-dragging={isDragging || undefined}
      style={{ transform: CSS.Transform.toString(transform && { ...transform, x: 0 }), transition }}
    >
      <button
        ref={setActivatorNodeRef}
        type="button"
        className="workspace-icon-button workspace-overview-drag"
        {...attributes}
        {...listeners}
        aria-label={`${phase.title} verslepen om te sorteren`}
        title="Versleep om te sorteren"
      >
        <GripVertical size={16} aria-hidden="true" />
      </button>
      <button
        type="button"
        className="workspace-overview-open"
        onClick={onOpen}
        aria-label={`Open investeringsoverzicht ${phase.title}`}
      >
        <span className="workspace-overview-summary">
          <strong>{phase.title}</strong>
          <span>
            {pricingTypes[phase.pricingType].label} ·{' '}
            {rowCount ? `${rowCount} ${rowCount === 1 ? 'regel' : 'regels'}` : 'Nog geen regels'}
          </span>
        </span>
        <span className="workspace-overview-summary-total">{total}</span>
        <ChevronRight size={16} aria-hidden="true" />
      </button>
      <button
        type="button"
        className="workspace-icon-button"
        aria-label={`${phase.title} dupliceren`}
        title="Dupliceren"
        onClick={onDuplicate}
      >
        <Copy size={15} />
      </button>
      <div className="workspace-overview-move">
        <button
          type="button"
          className="workspace-icon-button"
          disabled={index === 0}
          aria-label={`${phase.title} omhoog`}
          title="Omhoog"
          onClick={() => onMove(-1)}
        >
          <ArrowUp size={15} />
        </button>
        <button
          type="button"
          className="workspace-icon-button"
          disabled={index === count - 1}
          aria-label={`${phase.title} omlaag`}
          title="Omlaag"
          onClick={() => onMove(1)}
        >
          <ArrowDown size={15} />
        </button>
      </div>
    </li>
  );
}

export default function InvestmentOverviewsList({
  editor,
  blockId,
  phases,
}: {
  editor: Editor;
  blockId: string;
  phases: InvestmentDestination[];
}) {
  const [addedId, setAddedId] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const listRef = useRef<HTMLOListElement>(null);
  useEffect(() => {
    if (addedId) {
      listRef.current?.querySelector('[data-added="true"]')?.scrollIntoView({ block: 'nearest' });
    }
  }, [addedId, phases.length]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const reorder = (ids: string[], title: string) => {
    try {
      if (reorderInvestmentPhases(editor, blockId, ids)) {
        setAddedId(null);
        toast.success(`${title} verplaatst`);
      }
    } catch (error) {
      toast.error('Sorteren is niet gelukt', { error });
    }
  };
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setDragging(false);
    if (!over || active.id === over.id) return;
    const from = phases.findIndex((phase) => phase.tableId === active.id);
    const to = phases.findIndex((phase) => phase.tableId === over.id);
    if (from >= 0 && to >= 0)
      reorder(
        arrayMove(
          phases.map((phase) => phase.tableId),
          from,
          to
        ),
        phases[from].title
      );
  };

  return (
    <section
      className="workspace-overviews"
      aria-labelledby="workspace-overviews-title"
      data-investment-overviews-list
    >
      <header className="workspace-overviews-heading">
        <h4 id="workspace-overviews-title">
          Investeringsoverzichten <span>{phases.length}</span>
        </h4>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            try {
              setAddedId(addInvestmentPhaseToPage(editor, blockId));
              toast.success('Investeringsoverzicht toegevoegd');
            } catch (error) {
              toast.error('Toevoegen is niet gelukt', { error });
            }
          }}
        >
          <Plus size={14} /> Overzicht toevoegen
        </Button>
      </header>
      <p className="workspace-overview-hint">
        Open een overzicht om de regels te bewerken. Versleep de overzichten of gebruik de pijltjes
        om ze te ordenen; met het kopieericoon dupliceer je een overzicht.
      </p>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={() => setDragging(true)}
        onDragCancel={() => setDragging(false)}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={phases.map((phase) => phase.tableId)}
          strategy={verticalListSortingStrategy}
        >
          <ol
            ref={listRef}
            className="workspace-overviews-list"
            data-overview-sorting={dragging || undefined}
          >
            {phases.map((phase, index) => {
              const rows = investmentPhaseRows(editor.state.doc, phase);
              const rowCount = rows.filter(
                (row) => row.product || row.cells.some((cell) => cell.trim())
              ).length;
              const total = investmentPhaseTotal(editor.state.doc, phase);
              const totalLabel =
                (total.text ||
                  formatCurrencyRange(total.amount / 100, total.maxAmount / 100, phase.currency)) +
                (total.hasOnRequest ? ' + op aanvraag' : '');
              return (
                <OverviewItem
                  key={phase.tableId}
                  phase={phase}
                  index={index}
                  count={phases.length}
                  rowCount={rowCount}
                  total={totalLabel}
                  added={addedId === phase.tableId}
                  onOpen={() => {
                    const live = investmentDestinations(editor.state.doc).find(
                      (item) => item.tableId === phase.tableId
                    );
                    if (!live) return;
                    editor.view.dom.dataset.workspaceSelection = 'active';
                    editor.view.dispatch(
                      editor.state.tr.setSelection(
                        NodeSelection.create(editor.state.doc, live.headerPos)
                      )
                    );
                    const target = editor.view.nodeDOM(live.headerPos);
                    if (target instanceof HTMLElement) target.scrollIntoView({ block: 'nearest' });
                  }}
                  onDuplicate={() => {
                    try {
                      const copy = duplicateInvestmentPhase(editor, phase.tableId, {
                        keepPageSelected: true,
                      });
                      setAddedId(copy.tableId);
                      revealInvestmentPhase(editor, copy.tableId);
                      toast.success(`${copy.title} toegevoegd`, {
                        description: `Direct onder ${phase.title}.`,
                      });
                    } catch (error) {
                      toast.error('Dupliceren is niet gelukt', { error });
                    }
                  }}
                  onMove={(direction) =>
                    reorder(
                      arrayMove(
                        phases.map((item) => item.tableId),
                        index,
                        index + direction
                      ),
                      phase.title
                    )
                  }
                />
              );
            })}
          </ol>
        </SortableContext>
      </DndContext>
      {!phases.length && (
        <p className="workspace-overview-hint">
          Deze pagina heeft nog geen investeringsoverzichten. Voeg het eerste overzicht toe.
        </p>
      )}
      <p className="workspace-overview-price-note">Bedragen exclusief btw</p>
    </section>
  );
}
